import React from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Box, DataListItem } from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import { DateFormat } from 'constants/General'
import {
  ApprovalActionStatuses,
  ApprovalStatuses,
  PendingTransactionsResponse,
} from 'types/approvals'
import { SpecialPayments } from 'types/payments'
import { number2Ordinal } from 'utils/utils'

interface SidePanelBodyProps {
  transactionDetails: PendingTransactionsResponse
  tab?: string
}

export const SidePanelBody = ({ transactionDetails, tab }: SidePanelBodyProps) => {
  const { t } = useTranslation()

  const hasBeneficiaryDetails = !!transactionDetails?.beneficiaryDetails
  const hasDistinctFromAndToCurrency =
    transactionDetails?.currency?.code &&
    transactionDetails?.fromCurrency &&
    transactionDetails?.currency?.code !== transactionDetails?.fromCurrency
  const isCryptoPayout = transactionDetails?.type === SpecialPayments.CRYPTO_PAYOUT

  return (
    <Box>
      <h4 className="text-foreground-prominent text-h4">{t('transactionDetails')}</h4>
      <Box paddingT={8} paddingB={8}>
        {hasDistinctFromAndToCurrency && (
          <>
            <DataListItem
              id="amount-paid"
              testId="amount-paid"
              title={t('approvals.amountPaid')}
              body={`${transactionDetails?.fromAmount} ${transactionDetails?.fromCurrency}`}
            />
            {tab === ApprovalStatuses.PENDING && (
              <DataListItem
                id="beneficiary-to-receive"
                testId="beneficiary-to-receive"
                title={t('approvals.beneficiaryToReceive')}
                body={`${transactionDetails?.amount} ${transactionDetails?.currency?.code}`}
              />
            )}
            {tab === ApprovalStatuses.APPROVED && (
              <DataListItem
                id="beneficiary-received"
                testId="beneficiary-received"
                title={t('approvals.beneficiaryReceived')}
                body={`${transactionDetails?.amount} ${transactionDetails?.currency?.code}`}
              />
            )}
          </>
        )}
        {!hasDistinctFromAndToCurrency && (
          <DataListItem
            id="amount-paid"
            testId="amount-paid"
            title={isCryptoPayout ? t('approvals.amountPaid') : t('approvals.amountWithdrawn')}
            body={`${transactionDetails?.amount} ${transactionDetails?.currency?.code}`}
          />
        )}
        {!!transactionDetails?.userReference && (
          <DataListItem
            id="reference"
            testId="reference"
            title={t('reference')}
            body={ellipseMiddleText(transactionDetails?.userReference, 20, 10)}
            copyText={transactionDetails?.userReference}
          />
        )}
        <DataListItem
          id="wallet-name"
          testId="wallet-name"
          title={t('approvals.walletName')}
          body={transactionDetails?.walletName}
        />
        <DataListItem
          id="initiated-by"
          testId="initiated-by"
          title={t('approvals.initiatedBy')}
          body={transactionDetails?.initiatedBy}
        />

        {transactionDetails?.initiatedAt && (
          <DataListItem
            id="approvals-date-initiated"
            testId="approvals-date-initiated"
            title={t('approvals.dateInitiated')}
            body={dayjs(transactionDetails?.initiatedAt).format(DateFormat.D_MMM_YYYY_AT_HH_MM)}
          />
        )}
        {!!transactionDetails?.processedBy &&
          transactionDetails?.processedBy?.map((processedByDetail, index) => (
            <Box key={index}>
              {processedByDetail?.status ===
                ApprovalActionStatuses.PAYOUT_MERCHANT_APPROVAL_APPROVED && (
                <DataListItem
                  id={`approver-${index}`}
                  testId={`approver-${index}`}
                  title={`${number2Ordinal(index + 1)} ${t('approver')}`}
                  body={`${processedByDetail?.userFullName?.trim() || processedByDetail?.email}, ${dayjs(processedByDetail?.eventTime).format(DateFormat.D_MMM_YYYY_AT_HH_MM)} `}
                />
              )}
              {processedByDetail?.status ===
                ApprovalActionStatuses.PAYOUT_MERCHANT_APPROVAL_REJECTED && (
                <DataListItem
                  id={`approver-rejected-${index}`}
                  testId={`approver-rejected-${index}`}
                  title={t('approvals.rejectedBy')}
                  body={`${processedByDetail?.userFullName?.trim() || processedByDetail?.email}, ${dayjs(processedByDetail?.eventTime).format(DateFormat.D_MMM_YYYY_AT_HH_MM)} `}
                />
              )}
            </Box>
          ))}
        {!!transactionDetails?.actionedBy && tab === ApprovalStatuses.APPROVED && (
          <DataListItem
            id="approved-by"
            testId="approved-by"
            title={t('approvals.approvedBy')}
            body={transactionDetails?.actionedBy}
          />
        )}
        {!!transactionDetails?.actionedAt && tab === ApprovalStatuses.APPROVED && (
          <DataListItem
            id="approved-at"
            testId="approved-at"
            title={t('approvals.dateApproved')}
            body={dayjs(transactionDetails?.actionedAt).format(DateFormat.D_MMM_YYYY_AT_HH_MM)}
          />
        )}
        {!!transactionDetails?.actionedBy && tab === ApprovalStatuses.REJECTED && (
          <DataListItem
            id="rejected-by"
            testId="rejected-by"
            title={t('approvals.rejectedBy')}
            body={transactionDetails?.actionedBy}
          />
        )}
        {!!transactionDetails?.actionedAt &&
          !transactionDetails?.processedBy &&
          tab === ApprovalStatuses.REJECTED && (
            <DataListItem
              id="rejected-at"
              testId="rejected-at"
              title={t('approvals.dateRejected')}
              body={dayjs(transactionDetails?.actionedAt).format(DateFormat.D_MMM_YYYY_AT_HH_MM)}
            />
          )}
        {!!transactionDetails?.actionedAt && tab === ApprovalStatuses.EXPIRED && (
          <DataListItem
            id="expired-at"
            testId="expired-at"
            title={t('approvals.dateOfExpiry')}
            body={dayjs(transactionDetails?.actionedAt).format(DateFormat.D_MMM_YYYY_AT_HH_MM)}
          />
        )}
      </Box>
      {hasBeneficiaryDetails && (
        <>
          <h4 className="text-foreground-prominent text-h4">{t('beneficiaryDetails')}</h4>
          <Box gapY={8} paddingB={8}>
            {!!transactionDetails?.beneficiaryDetails?.destinationWallet && (
              <DataListItem
                id="destination-wallet"
                testId="destination-wallet"
                title={t('destinationWallet')}
                body={ellipseMiddleText(
                  transactionDetails?.beneficiaryDetails?.destinationWallet,
                  20,
                  10
                )}
                copyText={transactionDetails?.beneficiaryDetails?.destinationWallet}
              />
            )}
            {!!transactionDetails?.beneficiaryDetails?.bankCode && (
              <DataListItem
                id="bank-code"
                testId="bank-code"
                title={t('bankCode')}
                body={transactionDetails?.beneficiaryDetails?.bankCode}
              />
            )}
            {!!transactionDetails?.beneficiaryDetails?.accountNumber && (
              <DataListItem
                id="account-number"
                testId="account-number"
                title={t('accountNumber')}
                body={transactionDetails?.beneficiaryDetails?.accountNumber}
              />
            )}
            {!!transactionDetails?.beneficiaryDetails?.beneficiaryName && (
              <DataListItem
                id="beneficiary-name"
                testId="beneficiary-name"
                title={t('beneficiaryName')}
                body={transactionDetails?.beneficiaryDetails?.beneficiaryName}
              />
            )}
          </Box>
        </>
      )}
    </Box>
  )
}
